import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Tabs, message } from "antd";
import moment from "moment";
import { GetAuditoriaClinicaCitas } from "../../Service/Service.auditoriaClinica";
import BandejaFiltros from "../../Components/AuditoriaClinica/rediseno/BandejaFiltros";
import BandejaTabla from "../../Components/AuditoriaClinica/rediseno/BandejaTabla";
import {
    TABS_AUDITORIA,
    BANDEJA_FILTROS_VACIOS,
    construirParametros,
    saltoDirecto,
    hayFiltrosAplicados,
} from "../../utils/auditoriaBandeja";
import { usePageSubtitulo } from "../../context/PageHeaderContext";
import "../../styles/AuditoriaClinicaRediseno.css";

// AUDITORIA-CLINICA-REDISENO (FASE 3) -- bandeja de auditoría, la pantalla de
// entrada del módulo. Sirve `/auditoria-clinica`: sustituyó al listado clásico
// (tabla ancha + drawer + modal), que queda sin entrada de menú en
// `/auditoria-clinica/clasico` mientras dura la verificación.
//
// El filtro principal deja de ser un combo perdido entre otros diez y pasa a
// ser la pestaña: Pendientes / En proceso / Auditadas, cada una con su conteo.
// La pestaña "Con observación" de la maqueta NO existe: no hay dónde guardar
// una observación del auditor (FASE 0 §6, decisión confirmada).
// Tras el cambio de guardia, la bandeja ES `/auditoria-clinica`. El detalle
// cuelga de ella (`/auditoria-clinica/:idcita`).
export const RUTA_BANDEJA = "/auditoria-clinica";
const RUTA_ATENCION = "/auditoria-clinica";

// El endpoint no devuelve agregados por estado: el conteo de cada pestaña se
// pide con la MISMA consulta y `pageSize=1`, quedándose sólo con
// `pagination.total`. Son 3 llamadas ligeras por cambio de filtro, no una por
// fila.
const pedirConteo = async (filtros, estado) => {
    const res = await GetAuditoriaClinicaCitas({ page: 1, pageSize: 1, filters: construirParametros(filtros, estado) });
    return res?.success ? res.pagination?.total || 0 : null;
};

// `location.state` viaja por el history: los moment del rango se guardan como
// texto y se rehidratan al volver.
const serializarFiltros = (f) => ({
    ...f,
    rango: f.rango && f.rango[0] && f.rango[1] ? [f.rango[0].toISOString(), f.rango[1].toISOString()] : null,
});
const rehidratarFiltros = (f) => {
    if (!f) return { ...BANDEJA_FILTROS_VACIOS };
    return { ...BANDEJA_FILTROS_VACIOS, ...f, rango: f.rango ? [moment(f.rango[0]), moment(f.rango[1])] : null };
};

function AuditoriaBandeja() {
    const navigate = useNavigate();
    const location = useLocation();
    const retorno = location.state || {};

    const [tab, setTab] = useState(retorno.tab || "PENDIENTE");
    const [filtros, setFiltros] = useState(() => rehidratarFiltros(retorno.filtros));
    const [page, setPage] = useState(retorno.page || 1);
    const [pageSize, setPageSize] = useState(retorno.pageSize || 20);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [conteos, setConteos] = useState({});
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);
    const [activa, setActiva] = useState(retorno.idcitaActiva || null);

    const filtrosKey = useMemo(() => JSON.stringify(serializarFiltros(filtros)), [filtros]);

    const cargar = useCallback(
        async (pg, ps) => {
            setCargando(true);
            setError(null);
            const res = await GetAuditoriaClinicaCitas({ page: pg, pageSize: ps, filters: construirParametros(filtros, tab) });
            if (res?.success) {
                setRows(res.data || []);
                setTotal(res.pagination?.total || 0);
                setPage(res.pagination?.page || pg);
                setPageSize(res.pagination?.pageSize || ps);
            } else {
                setRows([]);
                setTotal(0);
                setError(res?.message || "No se pudo obtener el listado de auditoría.");
            }
            setCargando(false);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filtrosKey, tab]
    );

    useEffect(() => {
        cargar(page, pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cargar]);

    useEffect(() => {
        let cancelado = false;
        (async () => {
            const resultados = await Promise.all(TABS_AUDITORIA.map((t) => pedirConteo(filtros, t.key)));
            if (cancelado) return;
            setConteos(TABS_AUDITORIA.reduce((acc, t, i) => ({ ...acc, [t.key]: resultados[i] }), {}));
        })();
        return () => {
            cancelado = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtrosKey]);

    const cambiarFiltros = (patch) => {
        // "#3109" no es un filtro: es abrir esa atención directamente (el
        // endpoint no filtra por número de atención, ver FASE 0 §4).
        if (patch.q !== undefined) {
            const id = saltoDirecto(patch.q);
            if (id) {
                abrirAtencion({ idcita: id }, -1);
                return;
            }
        }
        setPage(1);
        setFiltros((f) => ({ ...f, ...patch }));
    };

    const limpiar = () => {
        setPage(1);
        setFiltros({ ...BANDEJA_FILTROS_VACIOS });
    };

    function abrirAtencion(fila, indice) {
        setActiva(fila.idcita);
        navigate(`${RUTA_ATENCION}/${fila.idcita}`, {
            state: {
                fila,
                // Cola = las atenciones de la página actual del conjunto
                // filtrado, para poder avanzar sin volver a la bandeja.
                cola: indice >= 0 ? { ids: rows.map((r) => r.idcita), filas: rows, indice } : null,
                retorno: {
                    ruta: RUTA_BANDEJA,
                    tab,
                    filtros: serializarFiltros(filtros),
                    page,
                    pageSize,
                    idcitaActiva: fila.idcita,
                },
            },
        });
    }

    const auditarSiguientePendiente = async () => {
        const res = await GetAuditoriaClinicaCitas({
            page: 1,
            pageSize: 1,
            filters: construirParametros(filtros, "PENDIENTE"),
        });
        const siguiente = res?.success ? (res.data || [])[0] : null;
        if (!siguiente) {
            message.info("No queda ninguna atención pendiente con los filtros aplicados.");
            return;
        }
        abrirAtencion(siguiente, -1);
    };

    // FASE 2: la barra superior muestra el conteo del filtro activo. Lo publica
    // la pantalla porque es la única que lo sabe.
    const etiquetaTab = (TABS_AUDITORIA.find((t) => t.key === tab) || {}).label || "";
    usePageSubtitulo(cargando ? "" : `${total} atenciones · ${etiquetaTab}`);

    const items = TABS_AUDITORIA.map((t) => ({
        key: t.key,
        label: `${t.label}${conteos[t.key] === undefined || conteos[t.key] === null ? "" : ` (${conteos[t.key]})`}`,
    }));

    return (
        <div className="page-content">
            <div className="container-fluid">
                <div className="ac-bandeja__cabecera">
                    <div>
                        <h1 className="ac-bandeja__titulo">Auditoría clínica</h1>
                        <span className="ac-progreso__texto">Atenciones finalizadas dentro de tu alcance</span>
                    </div>
                    <Button type="primary" onClick={auditarSiguientePendiente} data-testid="ac-bandeja-siguiente-pendiente">
                        Auditar la siguiente pendiente
                    </Button>
                </div>

                <Tabs
                    activeKey={tab}
                    items={items}
                    onChange={(k) => {
                        setPage(1);
                        setTab(k);
                    }}
                    data-testid="ac-bandeja-tabs"
                />

                <BandejaFiltros filtros={filtros} onCambio={cambiarFiltros} onLimpiar={limpiar} />

                <BandejaTabla
                    rows={rows}
                    loading={cargando}
                    error={error}
                    onReintentar={() => cargar(page, pageSize)}
                    page={page}
                    pageSize={pageSize}
                    total={total}
                    activa={activa}
                    hayFiltros={hayFiltrosAplicados(filtros)}
                    onLimpiarFiltros={limpiar}
                    onCambioPagina={(pg, ps) => cargar(pg, ps)}
                    onAbrir={(fila) => abrirAtencion(fila, rows.findIndex((r) => r.idcita === fila.idcita))}
                />
            </div>
        </div>
    );
}

export default AuditoriaBandeja;
